import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from '@/shared/ui';
import { formatRate } from '@/shared/lib/format-money';
import { useAuth } from '@/features/auth/auth-store';
import { useCurrencies } from '@/features/profile/use-me';
import { formatRateDate, latestAsOf, usableRatesByCode } from './rates';
import { useCurrency } from './useCurrency';
import { useFxRates } from './useFxRates';

const CURRENCY_NAMES: Record<string, string> = {
  RUB: 'Российский рубль',
  USD: 'Доллар США',
  EUR: 'Евро',
  CNY: 'Китайский юань',
  JPY: 'Японская йена',
  KRW: 'Южнокорейская вона',
};

function CurrencyBadge({ symbol, size }: { symbol: string; size: number }) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded bg-gradient-to-br from-[#8ed5ff] to-[#00506e] font-semibold text-[#00354a]"
      style={{ width: size, height: size, fontSize: size * 0.5 }}
    >
      {symbol}
    </span>
  );
}

/**
 * Display-currency picker: a radio menu (menuitemradio, roving focus, arrow keys)
 * that also shows each currency's official CBR rate. The rates are plain ЦБ РФ
 * rates; quotes may differ by the organization's FX markup / custom rates.
 */
export function CurrencyToggle() {
  const { status } = useAuth();
  const { data: currencies = [], isLoading: currenciesLoading } = useCurrencies();
  const { data: rates } = useFxRates();
  const { code, setCurrency, isPending } = useCurrency();

  if (status !== 'authed') return null;
  if (currenciesLoading && currencies.length === 0) return null;

  const active = currencies.find((c) => c.code === code);
  const rateByCode = usableRatesByCode(rates);
  const ratesDate = latestAsOf(rateByCode.values());

  function handleChange(newCode: string) {
    if (newCode !== code) setCurrency(newCode);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* While a switch saves, the trigger shows only a spinner: `code` comes from the
            `me` query, which refetches before the listings do, so showing it here would
            flip a beat ahead of the table. */}
        <button
          aria-label="Валюта расчёта цены"
          aria-busy={isPending}
          disabled={isPending}
          className="flex h-8 w-[80px] shrink-0 items-center justify-center gap-1.5 rounded-md bg-surface-high py-0 pl-2 pr-2.5 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-surface-highest focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          {isPending ? (
            <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <CurrencyBadge symbol={active?.symbol ?? code} size={20} />
              {code}
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[288px] p-2">
        <DropdownMenuLabel className="db-eyebrow px-2 pb-2 pt-1 font-normal">
          Валюта расчёта цены
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={code}
          onValueChange={handleChange}
          className="flex flex-col gap-0.5"
        >
          {currencies.map((c) => {
            const rate = rateByCode.get(c.code);
            return (
              // Radix item directly: the shared DropdownMenuRadioItem pins a dot
              // indicator to the left, where this design has the currency badge.
              <DropdownMenuPrimitive.RadioItem
                key={c.code}
                value={c.code}
                className="flex cursor-default select-none items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-surface-bright focus:bg-surface-bright data-[state=checked]:bg-primary/10 data-[state=checked]:ring-1 data-[state=checked]:ring-inset data-[state=checked]:ring-primary/30"
              >
                <CurrencyBadge symbol={c.symbol} size={26} />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-foreground">{c.code}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {CURRENCY_NAMES[c.code] ?? c.code}
                  </span>
                </span>
                {c.code === 'RUB' ? (
                  <span className="shrink-0 text-xs text-outline-faint">базовая</span>
                ) : rate ? (
                  <span className="shrink-0 text-xs text-outline-faint">
                    {formatRate(rate.rate_to_rub)} ₽
                  </span>
                ) : null}
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  <DropdownMenuPrimitive.ItemIndicator>
                    <Check className="h-4 w-4 text-primary" />
                  </DropdownMenuPrimitive.ItemIndicator>
                </span>
              </DropdownMenuPrimitive.RadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
        {ratesDate && (
          <p className="px-2 pb-1 pt-2 text-[11px] text-outline-faint">
            Курс ЦБ РФ на {formatRateDate(ratesDate)}
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
